<template>
  <div class="w-full">
    <div class="p-6 h-full overflow-y-auto">
      <form
        class="space-y-6"
        @submit.prevent="submit"
      >
        <!-- Basic Information -->
        <div class="space-y-4">
          <h3 class="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
            Basic Information
          </h3>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.name') }}</label>

            <AppInput
              v-model="packageDetailsForm.form.name"
              name="name"
              type="text"
              :error="packageDetailsForm.getErrors('name')"
              class="form-input"
              placeholder="Enter package name..."
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.subcontractor') }}</label>

            <AppTypeAhead
              v-if="packagesLite"
              v-model="packageDetailsForm.form.subcontractor_name"
              name="subcontractor_id"
              :suggestion-callback="getSuggestions"
              :initial-suggestions="getSuggestions"
              :error="packageDetailsForm.getErrors('subcontractor_id')"
              class="form-input"
              placeholder="Select subcontractor..."
              :disabled="selectedPackage.locked_at"
              @value-selected="selectSupplier"
            />

            <div
              v-else
              class="form-input bg-gray-50 text-gray-500"
            >
              {{ packageDetailsForm.form.subcontractor_name || 'Not assigned' }}
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.contractReference') }}</label>

            <AppInput
              v-model="packageDetailsForm.form.contract_reference"
              name="contract_reference"
              type="text"
              :error="packageDetailsForm.getErrors('contract_reference')"
              class="form-input"
              placeholder="Enter contract reference..."
            />
          </div>
        </div>

        <!-- Contract Dates -->
        <div class="space-y-4">
          <h3 class="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
            Contract Dates
          </h3>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.contractStartDate') }}</label>

              <AppInput
                v-model="packageDetailsForm.form.contract_start_date"
                name="contract_start_date"
                type="date"
                :error="packageDetailsForm.getErrors('contract_start_date')"
                class="form-input"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('Subcontract.PackagesDashboard.PackageBreakdown.label.contractEndDate') }}</label>

              <AppInput
                v-model="packageDetailsForm.form.contract_end_date"
                name="contract_end_date"
                type="date"
                :error="packageDetailsForm.getErrors('contract_end_date')"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <!-- Save Button -->
        <div class="pt-4 border-t border-gray-200">
          <button
            v-if="canSave"
            type="submit"
            class="btn btn--primary-blue w-full flex items-center justify-center space-x-2"
            :disabled="updateAction.is(States.LOADING)"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
</svg>

            <span>Save Package Details</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, falsey } from '@/helpers';
import { t } from '@/i18n';
import Package from '@/models/Package';
import { SupplierType } from '@/models/ProjectSupplier';
import { usePackagesStore } from '@/store/packages';
import { useProjectsStore } from '@/store/projects';
import { useSuppliersStore } from '@/store/supplier';
import { computed, nextTick, ref, watch } from 'vue';
import { toast } from 'vue3-toastify';

const props = defineProps<{
  selectedPackage: Package;
  canSave: boolean;
}>();

const packagesStore = usePackagesStore();
const projectsStore = useProjectsStore();
const suppliersStore = useSuppliersStore();
const updateAction = useStoreApiAction(packagesStore.update);

const packagesLite = computed(() => {
  return !!projectsStore.project?.feature_flag_subcontractor_packages_lite;
});

const packageDetailsForm = useFormData({
  name: '',
  subcontractor_id: undefined,
  subcontractor_name: '',
  contract_start_date: undefined,
  contract_end_date: undefined,
  contract_reference: '',
});

const suggestedSuppliers = ref<{ id: number; name: string }[]>([]);

watch(() => props.selectedPackage, (pkg, oldValue) => {
  if(!pkg) return;

  if(pkg.id === oldValue?.id) return;

  packageDetailsForm.setData(pkg, ['contract_end_date', 'contract_start_date']);
  packageDetailsForm.form.contract_end_date = dateForDateInput(pkg.contract_end_date);
  packageDetailsForm.form.contract_start_date = dateForDateInput(pkg.contract_start_date);

  if(falsey(packageDetailsForm.form.contract_end_date)) {
    packageDetailsForm.form.contract_end_date = dateForDateInput(projectsStore.project?.contract_end_date);
  }

  if(falsey(packageDetailsForm.form.contract_start_date)) {
    packageDetailsForm.form.contract_start_date = dateForDateInput(projectsStore.project?.contract_start_date);
  }

  packageDetailsForm.form.subcontractor_name = pkg.subcontractor?.name;
}, { immediate: true });

async function getSuggestions() {
  suggestedSuppliers.value = await suppliersStore.search({
    name: packageDetailsForm.form.subcontractor_name ?? '',
    project_id: projectsStore.project!.id,
    type: [SupplierType.subcontractor],
  });

  return suggestedSuppliers.value.map((s) => s.name);
}

function selectSupplier(index: number) {
  nextTick(() => {
    const supplier = suggestedSuppliers.value[index];

    if(supplier) packageDetailsForm.form.subcontractor_id = supplier.id;
  });
}

function submit() {
  packageDetailsForm.resetErrors();

  const fields = packagesLite.value ?
    ['name', 'subcontractor_id', 'contract_start_date', 'contract_end_date', 'contract_reference'] :
    ['name', 'contract_start_date', 'contract_end_date', 'contract_reference'];

  updateAction
    .request(
      props.selectedPackage.id,
      packageDetailsForm.only(fields),
    )
    .then(() => {
      toast.success(t('Subcontract.PackagesDashboard.PackageBreakdown.message.packageDetailsUpdated'));
    })
    .catch((err) => {
      packageDetailsForm.setErrors(err.data);
    });
}
</script>
